import React, { useState, useRef, useEffect } from 'react';
import { User, LogOut, Settings, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../services/auth';

interface UserMenuProps {
  user: { name?: string; email?: string; } | null | undefined;
  onLogout: () => void;
}

export default function UserMenu({ user, onLogout }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // User data helper functions
  const getUserName = () => user?.name || 'User';
  const getUserEmail = () => user?.email || 'No email provided';
  const getUserInitial = () => {
    if (user?.name && user.name.length > 0) {
      return user.name.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Enhanced logout handler
  const handleLogout = async () => {
    try {
      console.log("Starting logout process from UserMenu...");
      
      // Close the menu
      setIsOpen(false);
      
      // Call the logout function from auth service
      await logout();
      
      // Clean up local storage items individually
      localStorage.removeItem('auth_token');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userProfile');
      
      // Call the parent's onLogout callback (await so its redirect happens first)
      if (typeof onLogout === 'function') {
        await onLogout();
      }

      // Fallback: if for some reason no redirect occurred, send to marketing page
      setTimeout(() => {
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }, 100);
    } catch (error) {
      console.error('Error during logout:', error);
      
        // Still try to clean up and navigate even if the logout API call fails
      localStorage.removeItem('auth_token');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userProfile');
      
      // Fallback redirect on error
      window.location.href = '/';
    }
  };
  
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-full pl-3 pr-2 py-1.5 hover:bg-[#161513]/70 transition-colors text-gray-300 hover:text-white"
      >
        <div className="w-8 h-8 bg-[#0C5484] rounded-full flex items-center justify-center text-white font-medium">
          {getUserInitial()}
        </div>
        <span className="text-sm">{getUserName()}</span>
        <ChevronDown className={`w-4 h-4 text-gray-300 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-[#161513] rounded-xl shadow-xl border border-[#0CB4E4]/20 z-50 overflow-hidden">
          <div className="p-4 border-b border-[#0CB4E4]/20">
            <p className="font-medium text-gray-300">{getUserName()}</p>
            <p className="text-sm text-gray-300">{getUserEmail()}</p>
          </div>
          <div className="p-2">
            <button 
              className="w-full flex items-center gap-3 px-4 py-2 text-left rounded-lg hover:bg-[#161513] hover:text-white transition-colors text-gray-300"
              onClick={() => {
                setIsOpen(false);
                navigate('/update-profile');
              }}
            >
              <Settings className="w-5 h-5 text-gray-300" />
              <span>Account Settings</span>
            </button>
            <button 
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 text-left rounded-lg hover:bg-[#161513] transition-colors text-[#0CB4E4]"
            >
              <LogOut className="w-5 h-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
